import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'

import { useTranslation } from 'next-i18next'

import useGetCorrectedCountryCode from '~/curriculum/hooks/useGetCorrectedCountryCode'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { isAuthenticated } from '~/auth/slices/auth.slice'

import { useGetFamilyMemberRewardsQuery, useGetRewardsQuery } from '~/redux/apis/reward-api'
import { useAppSelector } from '~/redux/hooks'

import {
  useGetUserRecentCurriculumCountryQuery,
  useGetUserStatsSummaryQuery,
  useGetUserTimeQuery,
} from '~/statistics-v2/api/statistics.api'
import {
  combineTopicsStats,
  toAllMathHeaderStats,
  toGradeKey,
  toHeaderStats,
} from '~/statistics-v2/helper/statistics.helper'
import {
  HeaderStats,
  MasteryCertificate,
  MustKnowsProgressCardProps,
  OverallProgressCardProps,
  TopicStats,
  UserStats,
} from '~/statistics-v2/types/statistics.types'
import useTimeSpentReport from '~/dashboard/hooks/useTimeSpentReport'
import useLearningStats from '~/statistics/hooks/useLearningStats'
import { useGamesStatsQuery, useGetUnMappedActivitiesStatsQuery } from '~/redux/apis/user-stats-api'

/**
 * The pupil's own statistics panel, and the parent's view of a child (`userId`).
 *
 * 🔴 A change to what this panel counts must also reach the teacher's view of a pupil
 * (`~/teacher-panel/hooks/useGetStudentStats`, hom-api `/school/teacher/students/:id/*`), or differ there on
 * purpose. Test prep reaches all three inside `get-user-stats` (hom-api `progressService.getUserStats`).
 */
export function useGetUserStats(selectedGrade: string, skip = false, userId?: number): UserStats {
  const { t } = useTranslation('statisticsV2')
  const recentCurriculumCountry = useGetUserRecentCurriculumCountryQuery({ familyMemberId: userId })
  const timeSpentReport = useTimeSpentReport(userId)
  const currentUserCurriculumCountry = useGetUserChosenCurriculumCountryCode()
  const { data: unMappedActivitiesStats, isLoading: isUnMappedActivitiesStatsLoading } =
    useGetUnMappedActivitiesStatsQuery(userId ? { familyMemberId: userId } : undefined)

  const countryCode = useGetCorrectedCountryCode(
    userId ? recentCurriculumCountry.data?.countryCode || 'us' : currentUserCurriculumCountry,
  )
  /*
   * The stats request waits for its final country. Firing on the first render sent `countryCode=`
   * (own stats: the country is '' until the profile or `/misc/geo-location` answers) or a placeholder
   * `'us'` (family member: until `/get-recent-curriculum` answers), then again with the real one.
   */
  const isCountryReady = userId ? !recentCurriculumCountry.isLoading : countryCode !== ''

  const authenticated = useAppSelector(isAuthenticated)
  const { data: FamilyRewardsData } = useGetFamilyMemberRewardsQuery(
    { familyMemberId: userId },
    {
      skip: !authenticated,
      refetchOnMountOrArgChange: 10,
    },
  )

  const { data: currentUserRewardsData } = useGetRewardsQuery(userId ? { userId } : undefined, {
    skip: !authenticated,
    refetchOnMountOrArgChange: 10,
  })

  const isAllGradesSelected = selectedGrade === '0'
  /** '' until the page has picked its default grade — nothing grade-scoped may fire before that. */
  const isGradeChosen = selectedGrade !== ''

  const rewardsData = userId ? FamilyRewardsData : currentUserRewardsData
  const { videoWatchDurationInMs } = useLearningStats({ familyMemberId: userId })

  const userGrade = toGradeKey(countryCode, selectedGrade)

  /*
   * One request per selection: the chosen grade cut at topic level, or `overall` for All grades.
   * `currentData` for the figures, so a grade switch shows skeletons rather than the previous grade's
   * numbers; `data` (the last answer, whatever it was for) only for the dropdown's grade list.
   */
  const {
    data: lastUserStats,
    currentData: userStats,
    isError: isStatsError,
    isLoading: isStatsFirstLoad,
  } = useGetUserStatsSummaryQuery(
    skip || !isCountryReady || !isGradeChosen
      ? skipToken
      : { countryCode, userId, gradeId: isAllGradesSelected ? undefined : userGrade },
  )
  /*
   * Skeleton state for the figures: true whenever the current selection has no answer yet, including
   * after every grade switch (`currentData` is cleared on an arg change).
   *
   * A failed request deliberately falls through to the same empty cards as before (`stats` is null,
   * `isLoading` false) — an error and retry state is 869fe087x.
   */
  const isStatsLoading = !skip && !userStats && !isStatsError

  /*
   * Games for the selection being shown: '0' is all math, so it sends no scope and gets every game
   * ever played; a grade sends that grade and sees only what was played on it.
   *
   * Args are scope-only on purpose — `/user-stats/games` ignores a date range, and `GamesStats`
   * sends the same shape, so both callers share one cache entry instead of firing two requests.
   */
  const gamesScope = {
    familyMemberId: userId,
    ...(isAllGradesSelected ? {} : { grade: userGrade }),
  }

  const { data: gamesStats } = useGamesStatsQuery(gamesScope, { skip: !isGradeChosen })

  const { data: userTime, isLoading: isTimeLoading } = useGetUserTimeQuery(
    { countryCode, gradeId: userGrade, userId },
    { skip: isAllGradesSelected || skip || !isGradeChosen || !isCountryReady },
  )

  const isLoading = isStatsLoading || isTimeLoading || isUnMappedActivitiesStatsLoading
  /*
   * First load only — RTK's own `isLoading` flags stay false once a query has answered, even across an
   * arg change. The page gates the topic-detail view on this, NOT on `isLoading` above: a selection
   * change clears `currentData`, and gating on it would unmount an open detail view and drop the user
   * back to the grade list.
   */
  const isInitialLoading = isStatsFirstLoad || isTimeLoading || isUnMappedActivitiesStatsLoading

  const stats = useMemo(() => {
    if (!userStats) return null
    return (isAllGradesSelected ? userStats.overall : userStats.grades[userGrade]) ?? null
  }, [isAllGradesSelected, userGrade, userStats])

  const headerStats = useMemo<HeaderStats>(() => {
    // Games go through `toHeaderStats` rather than being folded here, so a later source cannot land in only
    // one of the four figures. Test prep is already in `stats`, on every grade and overall.
    const baseStats = toHeaderStats(stats, userTime, isAllGradesSelected, { games: gamesStats?.activityTotals })

    if (!isAllGradesSelected) return baseStats

    return toAllMathHeaderStats(
      baseStats,
      stats,
      {
        timeSpent: timeSpentReport.timeSpent || 0,
        totalXP: rewardsData?.xp || 0,
        videoWatchTime: videoWatchDurationInMs,
      },
      {
        attempts: unMappedActivitiesStats?.totalAttempt || 0,
        correct: unMappedActivitiesStats?.totalCorrect || 0,
      },
      gamesStats?.activityTotals,
    )
  }, [
    stats,
    isAllGradesSelected,
    timeSpentReport.timeSpent,
    rewardsData?.xp,
    videoWatchDurationInMs,
    unMappedActivitiesStats,
    userTime,
    gamesStats?.activityTotals,
  ])

  const overAllProgressStatsCardProps = useMemo<OverallProgressCardProps>(() => {
    if (!stats) {
      return {
        totalTopics: 0,
        completedTopics: 0,
        totalSubtopics: 0,
        completedSubtopics: 0,
        totalLevels: 0,
        completedLevels: 0,
      }
    }

    return {
      totalTopics: stats.totalTopics,
      completedTopics: stats.completedTopics,
      totalSubtopics: stats.totalSubtopics,
      completedSubtopics: stats.completedSubtopics,
      totalLevels: stats.totalLevels,
      completedLevels: stats.completedLevels,
    }
  }, [stats])

  const mustKnowsProgressCardProps = useMemo<MustKnowsProgressCardProps>(() => {
    if (!stats) {
      return {
        total: 0,
        completed: 0,
      }
    }

    return {
      total: stats.totalMustKnows || 0,
      completed: stats.completedMustKnows || 0,
    }
  }, [stats])

  const masteryCertificatesProgressProps = useMemo<MasteryCertificate[]>(() => {
    return stats?.MasteryCertificates || []
  }, [stats])

  const combinedTopics = useMemo<TopicStats[]>(() => {
    if (!userStats || isAllGradesSelected) return []
    const gradeDetails = userStats.grades[userGrade]
    if (!gradeDetails) return []
    return combineTopicsStats(gradeDetails)
  }, [isAllGradesSelected, userGrade, userStats])

  const gradeList = useMemo<Record<string, string>>(() => {
    if (!lastUserStats?.gradeListData) return {}
    return { '0': t('allGrades'), ...lastUserStats.gradeListData }
  }, [lastUserStats, t])

  return {
    countryCode,
    gradeList,
    headerStats,
    overAllProgressStatsCardProps,
    mustKnowsProgressCardProps,
    masteryCertificatesProgressProps,
    topicsStats: combinedTopics,
    isLoading,
    isInitialLoading,
  }
}
